import {
  Component,
  ChangeDetectionStrategy,
  OnInit,
  inject,
  signal,
  computed,
  DestroyRef,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { NzMessageService } from 'ng-zorro-antd/message';
import Swal from 'sweetalert2';
import { CambiarAreaModal } from '../../../components/cambiar-area-modal/cambiar-area-modal';
import { DocumentosModal } from '../../../components/documentos-modal/documentos-modal';
import { RH_ACTIONS } from '@shared/constants/rh-actions.constants';
import { EmpleadoService } from '@services/empleado.service';
import { EmpleadoList } from '@schemas/empleado.schema';

import {
  CellStack,
  PageHeader,
  StatChip,
  TableActions,
  type ActionButtonDef,
  DataTable,
  CellTemplateDirective,
  type ColumnDef,
  type TableAction,
} from '@intelishs-group/ui';

@Component({
  selector: 'app-empleados-lista',
  imports: [
    DataTable,
    CellTemplateDirective,
    TableActions,
    CambiarAreaModal,
    DocumentosModal,
    PageHeader,
    CellStack,
    StatChip,
  ],
  templateUrl: './empleados-lista.page.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class EmpleadosListaPage implements OnInit {
  private readonly empleadoService = inject(EmpleadoService);
  private readonly router = inject(Router);
  private readonly message = inject(NzMessageService);
  private readonly destroyRef = inject(DestroyRef);

  empleados = signal<EmpleadoList[]>([]);
  empleadosFiltrados = signal<EmpleadoList[]>([]);
  loading = signal(false);
  filtroActivo = '';

  // ── Modal "Cambiar Área" ──────────────────────────────────────────
  cambiarAreaVisible = signal(false);
  empleadoParaCambio = signal<EmpleadoList | null>(null);

  // ── Modal "Documentos" ────────────────────────────────────────────
  documentosVisible = signal(false);
  empleadoParaDocs = signal<EmpleadoList | null>(null);

  /**
   * Anchos calculados sobre el valor más largo de cada campo, para que el
   * contenido no se corte ni haga wrap (la excepción es la dirección: hay
   * valores de 50 caracteres, así que se trunca con tooltip en `cell-stack`).
   *
   * Antes eran 10 columnas sumando 1360px de mínimo: la tabla vivía con scroll
   * horizontal. Unificando en dos líneas quedan 6 columnas y ~960px.
   */
  columns: ColumnDef<EmpleadoList>[] = [
    // Abre la fila: el estado es la referencia con la que el usuario se ubica y
    // por la que filtra con los chips de arriba.
    //
    // Encabezado vacío y 44px: el contenido es un punto de 10px con tooltip, así
    // que la palabra "Estatus" era lo único que pedía espacio —el texto medía
    // más que el dato— y no aporta nada que el color no diga ya.
    {
      key: 'activo',
      header: '',
      width: '44px',
      align: 'center',
      type: 'status',
    },
    // Columna flexible: se queda con el ancho sobrante, que es donde rinde
    // (nombre de 33 chars sobre "MC#### · puesto" de 41).
    {
      key: 'nombreCompleto',
      header: 'Empleado',
      width: 'auto',
      type: 'custom',
      // El `cell-stack` no trunca solo: sin el flag el nombre largo ensancha
      // la columna y desborda la tabla en vez de recortarse.
      ellipsis: true,
      filterable: true,
      sortable: true,
    },
    // Va pegada a Empleado: nombre → puesto → área → dirección se lee como una
    // sola secuencia organizacional. También flexible, porque la dirección
    // llega a 50 chars y agradece el espacio sobrante para truncar menos.
    {
      key: 'area',
      header: 'Área / Dirección',
      width: 'auto',
      type: 'custom',
      // Aqui es donde mas rinde: la direccion llega a 50 chars.
      ellipsis: true,
      filterable: true,
    },
    // Identificador fiscal: se consulta puntualmente, no se escanea, así que
    // va después de los datos con los que se reconoce a la persona.
    // Encabezado centrado sobre la columna; el RFC en sí se queda a la
    // izquierda, alineado con el resto de los datos de texto.
    { key: 'rfc', header: 'RFC', width: '125px', headerAlign: 'center', filterable: true },
    // 130px y no 115px: "Fecha Ingreso" más la flecha de ordenamiento no
    // caben en 115, y la flecha se iba al siguiente renglón —el encabezado
    // ocupaba dos líneas y desalineaba la fila de títulos—.
    {
      key: 'fechaIngreso',
      header: 'Fecha Ingreso',
      width: '130px',
      type: 'date',
      sortable: true,
    },
    // 5 botones de 24px (contrato del theme) + 4 gaps de 6px = 144px, más el
    // padding de celda. Es la botonera más grande del sistema.
    {
      key: 'acciones',
      header: 'Acciones',
      width: '152px',
      align: 'center',
      type: 'custom',
    },
  ];

  /**
   * Buscables sin columna propia: CURP e ID interno siguen siendo localizables
   * (se pegan en el buscador para validar identidad) y dirección/puesto se
   * buscan aunque vivan en la línea meta de otra celda.
   */
  readonly extraFilterKeys = ['curp', 'idInterno', 'puesto', 'direccion'];

  /**
   * El Excel sí lleva el expediente completo: la tabla se compacta para leerse
   * en pantalla, pero el archivo es para trabajar los datos fuera. Repone lo
   * que en pantalla vive dentro de una celda unificada (nombre, área) o no
   * tiene columna (ID, CURP, puesto, dirección).
   */
  readonly exportColumns: ColumnDef<EmpleadoList>[] = [
    // En pantalla es un punto sin encabezado; en el archivo necesita nombre.
    { key: 'activo', header: 'Estatus', type: 'status' },
    { key: 'idInterno', header: 'ID Interno' },
    { key: 'nombreCompleto', header: 'Nombre Completo' },
    { key: 'curp', header: 'CURP' },
    { key: 'puesto', header: 'Puesto' },
    { key: 'area', header: 'Área' },
    { key: 'direccion', header: 'Dirección' },
  ];

  getActionsForRow(row: EmpleadoList): ActionButtonDef[] {
    return [
      RH_ACTIONS.ver,
      RH_ACTIONS.editar,
      // Cambiar Área y Documentos solo en empleados activos — para inactivos
      // no tiene sentido reasignar área antes del reingreso, ni gestionar
      // documentos nuevos.
      ...(row.activo ? [RH_ACTIONS.cambiarArea] : []),
      ...(row.activo ? [RH_ACTIONS.documentos] : []),
      ...(row.activo ? [RH_ACTIONS.desactivar] : [RH_ACTIONS.activar]),
    ];
  }

  onAction(event: TableAction<EmpleadoList>): void {
    switch (event.action) {
      case 'ver':
        this.verEmpleado(event.row.id);
        break;
      case 'editar':
        this.editarEmpleado(event.row.id);
        break;
      case 'cambiarArea':
        this.abrirCambiarArea(event.row);
        break;
      case 'documentos':
        this.abrirDocumentos(event.row);
        break;
      case 'desactivar':
        this.confirmarDesactivar(event.row);
        break;
      case 'activar':
        this.confirmarActivar(event.row);
        break;
    }
  }

  abrirCambiarArea(emp: EmpleadoList): void {
    this.empleadoParaCambio.set(emp);
    this.cambiarAreaVisible.set(true);
  }

  abrirDocumentos(emp: EmpleadoList): void {
    this.empleadoParaDocs.set(emp);
    this.documentosVisible.set(true);
  }

  /** Callback del modal cuando termina exitosamente — recarga la lista. */
  onAreaCambiada(): void {
    this.cargarEmpleados();
  }

  // ── Contadores ──────────────────────────────────────────────────
  totalEmpleados = computed(() => this.empleados().length);
  totalActivos = computed(() => this.empleados().filter((e) => e.activo).length);
  totalInactivos = computed(() => this.empleados().filter((e) => !e.activo).length);

  ngOnInit(): void {
    this.cargarEmpleados();
  }

  /**
   * @param forzar lo usa el botón "Refrescar": sin él la llamada devuelve la
   * caché del servicio y el botón no traía nada nuevo.
   */
  cargarEmpleados(forzar = false): void {
    this.loading.set(true);
    this.empleadoService
      .getAll(forzar)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (data) => {
          this.empleados.set(data);
          this.buscar();
          this.loading.set(false);
        },
        error: () => {
          this.message.error('Error al cargar empleados');
          this.loading.set(false);
        },
      });
  }

  buscar(): void {
    let resultado = this.empleados();

    if (this.filtroActivo !== '') {
      const activo = this.filtroActivo === 'true';
      resultado = resultado.filter((e) => e.activo === activo);
    }

    this.empleadosFiltrados.set(resultado);
  }

  nuevoEmpleado(): void {
    this.router.navigate(['/empleados/nuevo']);
  }

  verEmpleado(id: number): void {
    this.router.navigate(['/empleados/detalle', id]);
  }

  editarEmpleado(id: number): void {
    this.router.navigate(['/empleados/editar', id]);
  }

  async confirmarDesactivar(emp: EmpleadoList): Promise<void> {
    const result = await Swal.fire({
      title: '¿Desactivar este empleado?',
      text: emp.nombreCompleto,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Desactivar',
      cancelButtonText: 'Cancelar',
      buttonsStyling: false,
      customClass: {
        confirmButton: 'confirm-btnSweet',
        cancelButton: 'cancel-btnSweet',
        actions: 'swal-actions-spaced',
      },
    });
    if (result.isConfirmed) this.eliminarEmpleado(emp.id);
  }

  async confirmarActivar(emp: EmpleadoList): Promise<void> {
    const result = await Swal.fire({
      title: '¿Activar este empleado (reingreso)?',
      text: emp.nombreCompleto,
      icon: 'question',
      showCancelButton: true,
      confirmButtonText: 'Activar',
      cancelButtonText: 'Cancelar',
      buttonsStyling: false,
      customClass: {
        confirmButton: 'confirm-btnSweet',
        cancelButton: 'cancel-btnSweet',
        actions: 'swal-actions-spaced',
      },
    });
    if (result.isConfirmed) this.activarEmpleado(emp.id);
  }

  eliminarEmpleado(id: number): void {
    this.empleadoService
      .delete(id)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.message.success('Empleado desactivado');
          this.cargarEmpleados();
        },
        error: () => {
          this.message.error('Error al desactivar empleado');
        },
      });
  }

  activarEmpleado(id: number): void {
    this.empleadoService
      .activate(id)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.message.success('Empleado activado (reingreso)');
          this.cargarEmpleados();
        },
        error: () => {
          this.message.error('Error al activar empleado');
        },
      });
  }
}
